import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, Search, AlertTriangle, TimerReset, TrendingDown, PencilLine } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import InventorySubNav from "@/components/inventory/InventorySubNav";
import InventoryStatusBadge from "@/components/inventory/InventoryStatusBadge";
import EditItemDialog from "@/components/inventory/EditItemDialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

export default function InventoryList() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [alerts, setAlerts] = useState({ expired: 0, expiring_soon: 0, low_stock: 0 });
  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingItemId, setEditingItemId] = useState(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/inventory/locations").then(({ data }) => setLocations(data));
    api.get("/inventory/categories").then(({ data }) => setCategories(data));
    api.get("/inventory/alerts").then(({ data }) => setAlerts(data));
  }, []);

  const fetchItems = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    if (locationFilter !== "all") params.location_id = locationFilter;
    if (categoryFilter !== "all") params.category_id = categoryFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    const { data } = await api.get("/inventory/items", { params });
    setItems(data);
  }, [search, locationFilter, categoryFilter, statusFilter]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 250);
    return () => clearTimeout(t);
  }, [fetchItems]);

  const toggleStatusChip = (status) => setStatusFilter((prev) => (prev === status ? "all" : status));

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        <h1 className="font-display text-3xl text-amtm-text mb-2" data-testid="inventory-list-title">Medical Inventory</h1>
        <InventorySubNav />

        <div className="grid grid-cols-3 gap-3 mb-5">
          <button
            type="button"
            onClick={() => toggleStatusChip("expired")}
            data-testid="alert-chip-expired"
            className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-colors ${statusFilter === "expired" ? "border-amtm-error bg-amtm-error/10" : "border-amtm-border bg-amtm-surface hover:bg-amtm-bg"}`}
          >
            <AlertTriangle className="h-6 w-6 text-amtm-error" />
            <div><p className="text-xl font-bold text-amtm-text">{alerts.expired}</p><p className="text-xs text-amtm-textMuted">Expired</p></div>
          </button>
          <button
            type="button"
            onClick={() => toggleStatusChip("expiring_soon")}
            data-testid="alert-chip-expiring-soon"
            className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-colors ${statusFilter === "expiring_soon" ? "border-amtm-warning bg-amtm-warning/10" : "border-amtm-border bg-amtm-surface hover:bg-amtm-bg"}`}
          >
            <TimerReset className="h-6 w-6 text-amtm-warning" />
            <div><p className="text-xl font-bold text-amtm-text">{alerts.expiring_soon}</p><p className="text-xs text-amtm-textMuted">Expiring within 90 days</p></div>
          </button>
          <button
            type="button"
            onClick={() => toggleStatusChip("low_stock")}
            data-testid="alert-chip-low-stock"
            className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-colors ${statusFilter === "low_stock" ? "border-amtm-warning bg-amtm-warning/10" : "border-amtm-border bg-amtm-surface hover:bg-amtm-bg"}`}
          >
            <TrendingDown className="h-6 w-6 text-amtm-warning" />
            <div><p className="text-xl font-bold text-amtm-text">{alerts.low_stock}</p><p className="text-xs text-amtm-textMuted">Below reorder level</p></div>
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amtm-textMuted" />
            <Input placeholder="Search name or company" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-56" data-testid="inventory-search-input" />
          </div>
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger className="w-40" data-testid="inventory-location-filter"><SelectValue placeholder="Drawer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Drawers</SelectItem>
              {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40" data-testid="inventory-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="inventory-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="in_stock">In Stock</SelectItem>
              <SelectItem value="low_stock">Low Stock</SelectItem>
              <SelectItem value="out_of_stock">Out of Stock</SelectItem>
              <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {items === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
        ) : items.length === 0 ? (
          <p className="text-amtm-textMuted text-center py-12" data-testid="inventory-empty-state">No items found.</p>
        ) : (
          <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border" data-testid="inventory-items-list">
            {items.map((item) => (
              <div key={item.id} data-testid={`inventory-item-row-${item.id}`} className="flex items-center justify-between p-4 gap-3 flex-wrap hover:bg-amtm-bg transition-colors">
                <Link to={`/admin/inventory/items/${item.id}`} className="min-w-0 flex-1">
                  <p className="font-semibold text-amtm-text">{item.name}</p>
                  <p className="text-sm text-amtm-textMuted">{item.company_name || "—"} · {item.category_name} · {item.location_name}</p>
                </Link>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <p className="font-semibold text-amtm-text">{item.total_stock} {item.unit}</p>
                    <p className="text-xs text-amtm-textMuted">Expiry: {item.earliest_expiry || "—"}</p>
                  </div>
                  <InventoryStatusBadge status={item.status} testId={`inventory-item-status-${item.id}`} />
                  {isAdmin && (
                    <Button size="icon" variant="outline" onClick={() => setEditingItemId(item.id)} data-testid={`inventory-item-edit-button-${item.id}`}>
                      <PencilLine className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <EditItemDialog
          itemId={editingItemId}
          open={!!editingItemId}
          onOpenChange={(open) => !open && setEditingItemId(null)}
          onSaved={fetchItems}
        />
      </div>
    </div>
  );
}
